import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Eye, EyeOff, GraduationCap } from "lucide-react";

export default function InstituteLogin() {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user) {
    if (user.role === "institute_admin") {
      return <Navigate to="/institute" replace />;
    }
    if (user.role === "institute_staff") {
      // Institute Staff are not allowed in the portal — show an error.
      return <Navigate to="/institute/login?denied=staff" replace />;
    }
    return <Navigate to="/admin" replace />;
  }

  const isStaffDenied = typeof window !== "undefined" && window.location.search.includes("denied=staff");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const me = await login(username.trim(), password);
      if (me?.role === "institute_staff") {
        // Institute Staff role no longer has portal access — clear session.
        localStorage.removeItem("gsm_token");
        localStorage.removeItem("gsm_user");
        logout();
        setError(
          "Access denied. The Institute Staff role is no longer permitted to sign in to the institute portal. Please contact your Institute Admin.",
        );
        return;
      }
      if (me?.role !== "institute_admin") {
        // Reject trust admins — they should use the main /login page
        localStorage.removeItem("gsm_token");
        localStorage.removeItem("gsm_user");
        logout();
        setError(
          "This portal is for Institute Admins only. Trust administrators please use the main /login page.",
        );
        return;
      }
      navigate("/institute", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="institute-login-page"
      className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#f9f8f6]"
    >
      <div className="relative hidden lg:block overflow-hidden">
        <img
          alt="Campus"
          src="https://customer-assets.emergentagent.com/job_trust-admin-hub-1/artifacts/hc6ja172_36.jpg"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#3a5a40]/85 via-[#234333]/75 to-[#1c1a17]/80" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-sm bg-white flex items-center justify-center p-1 shadow-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_trust-admin-hub-1/artifacts/i2nc9e38_logo.jpeg"
                alt="GS Mandal crest"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="tracking-[0.25em] text-xs uppercase text-white/70">
              Institute Portal · GS Mandal
            </div>
          </div>
          <div className="space-y-6">
            <div className="serif-rule w-24 bg-white/40" />
            <h1 className="font-serif text-5xl leading-tight">
              Your institute,<br />
              <span className="italic text-[#d4af37]">your master data.</span>
            </h1>
            <p className="max-w-md text-white/75 text-sm leading-relaxed">
              Manage departments, sanctioned intake and approved fees for your institute.
              Each financial year, recorded with approvals and audit trail.
            </p>
          </div>
          <div className="flex items-center gap-2 text-white/60 text-xs tracking-wider uppercase">
            <GraduationCap size={14} strokeWidth={1.5} />
            Institute access only · Trust admins use the main login
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b] mb-4">
              Institute Sign-in
            </div>
            <h2 className="font-serif text-4xl text-[#1c1a17] leading-tight">
              Sign in to your institute
            </h2>
            <p className="mt-3 text-sm text-[#6b655b]">
              Use your Institute Admin credentials.
            </p>
          </div>

          {isStaffDenied && (
            <div
              data-testid="institute-login-staff-denied"
              className="mb-6 text-sm text-[#9e2a2b] border border-[#9e2a2b]/30 bg-[#9e2a2b]/5 rounded-sm px-3 py-2"
            >
              The Institute Staff role no longer has access to the institute portal. Please sign in with an Institute Admin account.
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-6" data-testid="institute-login-form">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
                Username or Email
              </label>
              <input
                data-testid="institute-login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-2 w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]"
                autoFocus
                required
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
                Password
              </label>
              <div className="relative mt-2">
                <input
                  data-testid="institute-login-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-4 py-3 pr-11 focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]"
                  required
                />
                <button
                  type="button"
                  data-testid="institute-login-toggle"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b655b] hover:text-[#1c1a17]"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && (
              <div
                data-testid="institute-login-error"
                className="text-sm text-[#9e2a2b] border border-[#9e2a2b]/30 bg-[#9e2a2b]/5 rounded-sm px-3 py-2"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="institute-login-submit"
              className="w-full bg-[#234333] hover:bg-[#1b3326] text-white text-sm tracking-wide uppercase py-3 rounded-sm transition-colors disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-[11px] text-[#6b655b] text-center pt-4">
              Forgot your password? Ask your Institute Admin to reset it.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
